import { useEffect, useState, useRef, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Play, Pause, RotateCcw, MapPin, Truck, Navigation, Loader2, Radio } from "lucide-react";
import { toast } from "sonner";

// Simulated demo routes (normalized 0..100 coordinates on the fleet map)
const VEHICLES = [
  { code: "LFM-001", driver: "Marcus Reed", color: "#C8202F", route: [[8, 50], [25, 40], [42, 46], [60, 32], [80, 38], [92, 22]] },
  { code: "LFM-002", driver: "Alicia Bennett", color: "#141414", route: [[10, 12], [28, 20], [40, 34], [55, 40], [72, 30], [90, 44]] },
  { code: "LFM-003", driver: "Jordan Blake", color: "#C8A24A", route: [[6, 80], [22, 68], [38, 72], [56, 60], [74, 66], [94, 54]] },
  { code: "LFM-004", driver: "Dana Cruz", color: "#2563eb", route: [[12, 34], [30, 52], [48, 44], [66, 54], [82, 42], [95, 60]] },
  { code: "LFM-005", driver: "Sam Rivera", color: "#059669", route: [[5, 60], [20, 50], [36, 58], [52, 48], [70, 54], [88, 40]] },
];

function lerp(a, b, t) { return a + (b - a) * t; }
function posAt(route, p) {
  const segs = route.length - 1;
  const f = Math.min(Math.max(p, 0), 0.9999) * segs;
  const i = Math.floor(f);
  const t = f - i;
  return [lerp(route[i][0], route[i + 1][0], t), lerp(route[i][1], route[i + 1][1], t)];
}

export default function GpsTracking() {
  const [progress, setProgress] = useState(() => VEHICLES.map((_, i) => i * 0.08));
  const [playing, setPlaying] = useState(false);
  const [selected, setSelected] = useState(0);
  const [kpi, setKpi] = useState({ vehicles: 5, active: 0, drivers: 0, maint: 0 });
  const timer = useRef(null);

  useEffect(() => {
    api.get("/vehicles").then((r) => {
      const v = r.data || [];
      setKpi((k) => ({ ...k, vehicles: v.length || 5, active: v.filter((x) => x.status === "IN_USE").length }));
    }).catch(() => {});
    Promise.all([api.get("/trips").catch(() => ({ data: [] })), api.get("/vehicle-maintenance").catch(() => ({ data: [] }))])
      .then(([t, m]) => setKpi((k) => ({ ...k, active: t.data.filter((x) => x.status === "IN_PROGRESS").length || 3, drivers: 4, maint: m.data.filter((x) => ["DUE", "OVERDUE"].includes(x.status)).length })));
  }, []);

  const tick = useCallback(() => {
    setProgress((prev) => prev.map((p, i) => {
      const np = p + 0.004 + (i % 3) * 0.001;
      return np >= 1 ? 1 : np;
    }));
  }, []);

  useEffect(() => {
    if (playing) { timer.current = setInterval(tick, 100); return () => clearInterval(timer.current); }
  }, [playing, tick]);

  const allDone = progress.every((p) => p >= 1);
  const start = () => { if (allDone) setProgress(VEHICLES.map(() => 0)); setPlaying(true); };
  const reset = () => { setPlaying(false); setProgress(VEHICLES.map((_, i) => i * 0.08)); };

  const speed = (i) => playing && progress[i] < 1 ? Math.round(28 + Math.abs(Math.sin(progress[i] * 6)) * 22) : 0;

  return (
    <div data-testid="gps-tracking">
      <PageHeader title="Fleet GPS Tracking"
        subtitle="Live fleet positions and routes."
        actions={<span className="inline-flex items-center gap-1.5 rounded-full bg-ochre/15 text-ochre text-xs font-bold px-3 py-1.5"><Radio className="w-3.5 h-3.5" /> DEMO GPS — SIMULATED</span>} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {[["Total Vehicles", kpi.vehicles], ["Active Trips", kpi.active || 3], ["Drivers On Duty", kpi.drivers || 4], ["Maintenance Due", kpi.maint]].map(([l, v]) => (
          <div key={l} className="border border-border rounded-lg bg-card p-4"><p className="text-[11px] uppercase tracking-wider text-muted-foreground">{l}</p><p className="font-heading text-2xl font-bold mt-1">{v}</p></div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="border border-border rounded-xl bg-card overflow-hidden">
            {/* Controls */}
            <div className="flex items-center gap-2 p-3 border-b border-border bg-muted/40">
              {!playing ? <Button size="sm" onClick={start} data-testid="sim-start"><Play className="w-4 h-4 mr-1.5" /> {allDone ? "Restart" : "Start Simulation"}</Button>
                : <Button size="sm" variant="outline" onClick={() => setPlaying(false)} data-testid="sim-pause"><Pause className="w-4 h-4 mr-1.5" /> Pause</Button>}
              {!playing && !allDone && progress.some((p) => p > 0) && <Button size="sm" onClick={() => setPlaying(true)} data-testid="sim-resume"><Play className="w-4 h-4 mr-1.5" /> Resume</Button>}
              <Button size="sm" variant="ghost" onClick={reset} data-testid="sim-reset"><RotateCcw className="w-4 h-4 mr-1.5" /> Reset</Button>
              <span className="ml-auto text-xs text-muted-foreground">Columbus Metro · Ohio</span>
            </div>
            {/* Map */}
            <div className="relative bg-[#eef2f0]" style={{ aspectRatio: "16/10" }} data-testid="fleet-map">
              <svg viewBox="0 0 100 60" className="absolute inset-0 w-full h-full">
                <defs>
                  <pattern id="grid" width="8" height="8" patternUnits="userSpaceOnUse">
                    <path d="M 8 0 L 0 0 0 8" fill="none" stroke="#d7ddd9" strokeWidth="0.3" />
                  </pattern>
                </defs>
                <rect width="100" height="60" fill="url(#grid)" />
                {/* faux roads */}
                <path d="M0 30 H100 M50 0 V60 M0 15 L100 45 M0 50 L100 10" stroke="#cdd5d0" strokeWidth="0.8" fill="none" />
                {VEHICLES.map((v, i) => (
                  <polyline key={v.code} points={v.route.map((p) => p.join(",")).join(" ")} fill="none"
                    stroke={v.color} strokeWidth={selected === i ? 0.9 : 0.5} strokeDasharray="1.5 1.5" opacity={selected === i ? 0.9 : 0.4} />
                ))}
                {VEHICLES.map((v, i) => {
                  const [x, y] = posAt(v.route, progress[i]);
                  return (
                    <g key={v.code} onClick={() => setSelected(i)} style={{ cursor: "pointer" }}>
                      <circle cx={x} cy={y} r={selected === i ? 2.4 : 1.9} fill={v.color} stroke="#fff" strokeWidth="0.5" />
                      <text x={x} y={y - 3} fontSize="2.2" textAnchor="middle" fill="#141414" fontWeight="700">{v.code}</text>
                    </g>
                  );
                })}
              </svg>
            </div>
          </div>
        </div>

        {/* Telemetry */}
        <div className="space-y-3">
          {VEHICLES.map((v, i) => (
            <button key={v.code} onClick={() => setSelected(i)} data-testid="fleet-vehicle"
              className={`w-full text-left border rounded-lg bg-card p-4 transition-colors ${selected === i ? "border-primary" : "border-border hover:border-primary/40"}`}>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 font-semibold"><span className="w-2.5 h-2.5 rounded-full" style={{ background: v.color }} /> {v.code}</span>
                <StatusBadge value={progress[i] >= 1 ? "COMPLETED" : playing ? "IN_USE" : "PARKED"} />
              </div>
              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5"><Truck className="w-3.5 h-3.5" /> {v.driver}</p>
              <div className="flex items-center justify-between mt-2 text-xs">
                <span className="flex items-center gap-1 text-brand"><Navigation className="w-3.5 h-3.5" /> {speed(i)} mph</span>
                <span className="text-muted-foreground">{Math.round(progress[i] * 100)}% route</span>
              </div>
              <div className="h-1.5 rounded-full bg-muted mt-2 overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${progress[i] * 100}%`, background: v.color }} /></div>
            </button>
          ))}
          <p className="text-[11px] text-muted-foreground text-center">Simulated telemetry · updates every 100ms</p>
        </div>
      </div>
    </div>
  );
}
